import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';
import {
  Payment,
  PaymentBankInfo,
  PaymentStatus,
  usePaymentSettings,
} from '@/hooks/usePayments';

// Re-export so the admin module is the single import surface for the payments UI.
export { usePaymentSettings };
export type { Payment, PaymentBankInfo, PaymentStatus, PaymentSettings } from '@/hooks/usePayments';

export type AdminPaymentFilter = PaymentStatus | 'all';

// Shape returned by the admin_list_payments RPC: a payment row plus the joined
// couple identity (wedding_invitations) and payer email (auth.users).
export interface AdminPaymentRow extends Payment {
  groom_name: string | null;
  bride_name: string | null;
  unique_slug: string | null;
  payer_email: string | null;
}

// AdminPaymentRow with a short-lived signed URL resolved for the receipt.
export interface AdminPayment extends AdminPaymentRow {
  receiptSignedUrl: string | null;
}

const RECEIPTS_BUCKET = 'payment-receipts';
const QR_BUCKET = 'invitation-photos';
const RECEIPT_SIGNED_URL_TTL_SECONDS = 60 * 10; // 10 minutes

const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);

// Batch-sign receipt storage paths. Values already stored as a full http(s)
// URL (legacy/edge cases) are skipped and passed through by the caller.
async function signReceiptUrls(rows: AdminPaymentRow[]): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const paths = rows
    .map((row) => row.receipt_url)
    .filter((url): url is string => !!url && !isHttpUrl(url));

  if (paths.length === 0) return map;

  const { data, error } = await supabase.storage
    .from(RECEIPTS_BUCKET)
    .createSignedUrls(paths, RECEIPT_SIGNED_URL_TTL_SECONDS);

  if (error) {
    console.error('Error signing receipt URLs:', error);
    return map;
  }

  for (const item of data ?? []) {
    if (item.path && item.signedUrl) map.set(item.path, item.signedUrl);
  }
  return map;
}

// Admin-wide payment list, newest first, optionally filtered by status.
export function useAllPayments(filter: AdminPaymentFilter = 'pending') {
  return useQuery({
    queryKey: ['admin-payments', filter],
    queryFn: async (): Promise<AdminPayment[]> => {
      const { data, error } = await supabase.rpc('admin_list_payments', {
        p_status: filter === 'all' ? undefined : filter,
      });
      if (error) throw error;

      const rows = ((data as unknown as AdminPaymentRow[]) ?? []);
      const signed = await signReceiptUrls(rows);

      return rows.map((row) => ({
        ...row,
        receiptSignedUrl: !row.receipt_url
          ? null
          : isHttpUrl(row.receipt_url)
            ? row.receipt_url
            : signed.get(row.receipt_url) ?? null,
      }));
    },
  });
}

// Lightweight count for the sidebar/tab badge. Admin RLS lets admins read every
// payment row, so an exact head count is sufficient and cheap.
export function usePendingPaymentsCount() {
  return useQuery({
    queryKey: ['admin-payments', 'count', 'pending'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('payments')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (error) throw error;
      return count ?? 0;
    },
  });
}

// Approve a payment. The DB function also flips wedding_invitations.is_paid = true
// for the linked invitation (SECURITY DEFINER, bypassing the owner guard).
export function useApprovePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentId: string) => {
      const { data, error } = await supabase.rpc('approve_payment', {
        p_payment_id: paymentId,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payments'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['invitation'] });
    },
  });
}

// Reject a payment with a required reason.
export function useRejectPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ paymentId, reason }: { paymentId: string; reason: string }) => {
      const { data, error } = await supabase.rpc('reject_payment', {
        p_payment_id: paymentId,
        p_rejection_reason: reason,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payments'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

export interface UpdatePaymentSettingsInput {
  publishPriceUsd: number;
  usdToBsRate: number;
  bankInfo: PaymentBankInfo;
}

// Upsert the three payment-related app_settings keys (admin-only via RLS).
export function useUpdatePaymentSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdatePaymentSettingsInput) => {
      const rows: { key: string; value: Json }[] = [
        { key: 'payment.publish_price_usd', value: input.publishPriceUsd },
        { key: 'payment.usd_to_bs_rate', value: input.usdToBsRate },
        { key: 'payment.bank_info', value: input.bankInfo as unknown as Json },
      ];

      const { error } = await supabase
        .from('app_settings')
        .upsert(rows, { onConflict: 'key' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-settings'] });
    },
  });
}

// Slugify a filename to a storage-safe name while keeping the extension.
const safeFileName = (name: string): string => {
  const dot = name.lastIndexOf('.');
  const base = (dot > 0 ? name.slice(0, dot) : name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'qr';
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
  return ext ? `${base}.${ext}` : base;
};

// Upload a QR image to the public invitation-photos bucket and return its public
// URL. Couples need to read this from their banner/modal, so it must be public.
export async function uploadPaymentQr(file: File): Promise<string> {
  const path = `payment-qr/${Date.now()}-${safeFileName(file.name)}`;

  const { error } = await supabase.storage
    .from(QR_BUCKET)
    .upload(path, file, { contentType: file.type || undefined, upsert: true });

  if (error) throw new Error('No se pudo subir el QR: ' + error.message);

  const { data } = supabase.storage.from(QR_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}
